import { useRef, useState, useTransition } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Barcode as BarcodeIcon,
  Boxes,
  EyeOff,
  FileSpreadsheet,
  RotateCcw,
  ScanLine,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
} from '../ui/index.js';
import { fetchJson } from '../lib/fetch-json.js';
import { money, stamp, toCapitalizedCase } from '../lib/format.js';

interface DecisionProduct {
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly images: readonly string[];
  readonly purchasePriceHt?: number | null;
  readonly barcodeRaw?: string | null;
  readonly eanRaw?: string | null;
  readonly gtinCanonical?: string | null;
  readonly isVisible?: boolean;
}

interface PackDecisionItem {
  readonly id: number;
  readonly productId: number;
  readonly gtin: string | null;
  readonly status: string;
  readonly algoVersion: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string;
  readonly product: DecisionProduct;
  readonly isVisible?: boolean;
}

interface PaginatedPackResponse {
  readonly items: readonly PackDecisionItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly totalPages: number;
}

interface BrandRoster {
  readonly brands: readonly { readonly brand: string | null; readonly count: number }[];
  readonly total: number;
}

export function WorkbenchPackPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [, startTransition] = useTransition();

  // Filters state
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'name' | 'brand' | 'price'>('recent');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);

  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Scanner modal state
  const [scannerModalItem, setScannerModalItem] = useState<PackDecisionItem | null>(null);
  const [scannerCodeInput, setScannerCodeInput] = useState('');
  const scannerInputRef = useRef<HTMLInputElement>(null);

  // Fetch pack brands
  const brandsQuery = useQuery({
    queryKey: ['operator-brands', 'pack'],
    queryFn: () => fetchJson<BrandRoster>('/api/v1/review/operator-brands?status=pack'),
  });

  // Fetch paginated pack products
  const packQuery = useQuery({
    queryKey: ['decisions', 'pack', appliedSearch, selectedBrand, sortBy, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('status', 'pack');
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (appliedSearch.trim() !== '') params.set('q', appliedSearch.trim());
      if (selectedBrand.trim() !== '') params.set('brand', selectedBrand.trim());
      params.set('sortBy', sortBy);
      return fetchJson<PaginatedPackResponse>(`/api/v1/review/decisions?${params.toString()}`);
    },
  });

  // Remove from pack mutation (returns to review queue / normal status)
  const removeFromPackMutation = useMutation({
    mutationFn: (productId: number) =>
      fetchJson<{ ok: boolean }>(`/api/v1/products/${productId}/operator-status`, {
        method: 'POST',
        body: JSON.stringify({ value: 'none' }),
      }),
    onSuccess: () => {
      setActionFeedback('Produit retiré du Pack et réintégré dans le catalogue actif.');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
    onError: (err) => {
      setActionFeedback(`Erreur : ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  // Bulk remove from pack
  const bulkRemoveFromPackMutation = useMutation({
    mutationFn: async (productIds: number[]) => {
      for (const id of productIds) {
        await fetchJson<{ ok: boolean }>(`/api/v1/products/${id}/operator-status`, {
          method: 'POST',
          body: JSON.stringify({ value: 'none' }),
        });
      }
    },
    onSuccess: () => {
      setActionFeedback(`${selectedIds.size} produit(s) retiré(s) du Pack.`);
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
  });

  // Hide mutation
  const hideMutation = useMutation({
    mutationFn: (productId: number) =>
      fetchJson<{ ok: boolean }>(`/api/v1/products/${productId}/operator-status`, {
        method: 'POST',
        body: JSON.stringify({ value: 'hide' }),
      }),
    onSuccess: () => {
      setActionFeedback('Produit masqué sur WooCommerce.');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
  });

  // Barcode override from scanner modal
  const barcodeOverrideMutation = useMutation({
    mutationFn: (input: { productId: number; barcode: string }) =>
      fetchJson<{ ok: boolean; gtin: string }>(`/api/v1/products/${input.productId}/barcode-override`, {
        method: 'POST',
        body: JSON.stringify({ barcode: input.barcode }),
      }),
    onSuccess: (res) => {
      setActionFeedback(`Code-barres ${res.gtin} enregistré et produit validé avec succès.`);
      setScannerModalItem(null);
      setScannerCodeInput('');
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['operator-brands'] });
    },
    onError: (err) => {
      setActionFeedback(`Erreur lors de l'enregistrement du code-barres : ${err instanceof Error ? err.message : String(err)}`);
    },
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startTransition(() => {
      setAppliedSearch(search);
      setPage(1);
    });
  };

  const handleClearSearch = () => {
    setSearch('');
    startTransition(() => {
      setAppliedSearch('');
      setPage(1);
    });
  };

  const handleBrandChange = (brand: string) => {
    startTransition(() => {
      setSelectedBrand(brand);
      setPage(1);
    });
  };

  const handleSortChange = (newSort: typeof sortBy) => {
    startTransition(() => {
      setSortBy(newSort);
      setPage(1);
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    startTransition(() => {
      setPageSize(newSize);
      setPage(1);
    });
  };

  const data = packQuery.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(items.map((i) => i.productId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleSelectItem = (productId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleExportCsv = () => {
    const targetItems =
      selectedIds.size > 0 ? items.filter((i) => selectedIds.has(i.productId)) : items;
    if (targetItems.length === 0) return;

    const headers = ['ID', 'SKU', 'Nom', 'Marque', 'Code Woo', 'Date Mise en Pack', 'Opérateur', 'Prix Achat HT'];
    const rows = targetItems.map((d) => [
      d.productId,
      d.product.sku ?? '',
      `"${(d.product.name ?? '').replace(/"/g, '""')}"`,
      `"${(d.product.brand ?? '').replace(/"/g, '""')}"`,
      d.product.barcodeRaw ?? d.product.eanRaw ?? '',
      d.decidedAt ? new Date(d.decidedAt).toLocaleDateString('fr-FR') : '',
      `"${(d.decidedBy ?? '').replace(/"/g, '""')}"`,
      d.product.purchasePriceHt ?? '',
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `produits-pack-page-${page}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setActionFeedback(`Fichier CSV téléchargé avec succès (${targetItems.length} lignes).`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Produits Pack (Hors Matching)"
        subtitle="Produits mis de côté en Pack : exclus du matching GTIN et des exports Sage 100c, mais conservés en ligne et vendables sur le site WooCommerce."
        meta={[
          { label: 'Total en Pack', value: `${total.toLocaleString('fr-FR')} produits` },
          { label: 'Page actuelle', value: `${page} / ${totalPages}` },
          { label: 'Sélectionnés', value: `${selectedIds.size} sur la page` },
        ]}
        actions={
          <Button
            type="button"
            variant="soft"
            size="sm"
            onClick={handleExportCsv}
            disabled={items.length === 0}
            icon={<FileSpreadsheet className="h-4 w-4" />}
            title="Exporter les produits en pack affichés ou sélectionnés au format CSV"
          >
            Exporter CSV {selectedIds.size > 0 && `(${selectedIds.size})`}
          </Button>
        }
      />

      {actionFeedback && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-xs text-ink shadow-2xs">
          <span>{actionFeedback}</span>
          <button
            type="button"
            onClick={() => setActionFeedback(null)}
            className="text-ink-soft hover:text-ink"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters Bar */}
      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Search form */}
            <form onSubmit={handleSearchSubmit} className="flex flex-1 min-w-[260px] max-w-md items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par nom, SKU, code Woo…"
                  className="w-full rounded-md border border-border bg-paper pl-9 pr-8 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-500 focus:outline-hidden"
                />
                {search && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink"
                    title="Effacer la recherche"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button type="submit" variant="soft" size="sm">
                Filtrer
              </Button>
            </form>

            {/* Brand filter */}
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-3.5 w-3.5 text-ink-soft" />
              <select
                value={selectedBrand}
                onChange={(e) => handleBrandChange(e.target.value)}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value="">Toutes les marques ({brandsQuery.data?.total ?? '…'})</option>
                {brandsQuery.data?.brands.map((b) => (
                  <option key={b.brand ?? '__none__'} value={b.brand ?? '__NOBRAND__'}>
                    {b.brand ?? 'Sans marque'} ({b.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Sort & PageSize */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value as typeof sortBy)}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value="recent">Plus récents d&apos;abord</option>
                <option value="oldest">Plus anciens d&apos;abord</option>
                <option value="name">Nom (A → Z)</option>
                <option value="brand">Marque (A → Z)</option>
                <option value="price">Prix d&apos;achat décroissant</option>
              </select>

              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="rounded-md border border-border bg-paper px-2.5 py-1.5 text-xs text-ink focus:border-herb-500 focus:outline-hidden"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Bulk Action Bar (when selected) */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-2.5 shadow-2xs">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="select-all-pack"
              checked={items.length > 0 && selectedIds.size === items.length}
              onChange={handleSelectAll}
              className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500"
            />
            <label htmlFor="select-all-pack" className="text-xs font-semibold text-ink cursor-pointer">
              {selectedIds.size > 0
                ? `${selectedIds.size} / ${items.length} sélectionné(s)`
                : `Tout sélectionner (${items.length} sur la page)`}
            </label>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="soft"
                size="sm"
                onClick={() => bulkRemoveFromPackMutation.mutate([...selectedIds])}
                disabled={bulkRemoveFromPackMutation.isPending}
                icon={<RotateCcw className="h-3.5 w-3.5 text-herb-600" />}
              >
                Retirer du Pack ({selectedIds.size})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Main List */}
      {packQuery.isLoading ? (
        <Loading label="Chargement des produits en Pack…" />
      ) : items.length === 0 ? (
        <EmptyState
          title="Aucun produit en Pack trouvé"
          hint={
            appliedSearch || selectedBrand
              ? 'Aucun résultat ne correspond à vos filtres actuels. Modifiez la recherche ou la marque.'
              : 'Aucun produit n\'est actuellement mis de côté en Pack.'
          }
        />
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const prod = item.product;
            const isSelected = selectedIds.has(item.productId);
            const isRemoving = removeFromPackMutation.isPending && removeFromPackMutation.variables === item.productId;
            const isHiding = hideMutation.isPending && hideMutation.variables === item.productId;

            return (
              <div
                key={item.id}
                className={`rounded-xl border p-4 shadow-2xs transition-all ${
                  isSelected ? 'border-herb-400 bg-herb-50/20' : 'border-border bg-card hover:border-border-soft'
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  {/* Left: Checkbox + Thumbnail + Details */}
                  <div className="flex items-center gap-3.5 min-w-0 flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectItem(item.productId)}
                      className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500 shrink-0"
                    />

                    {/* Image with scanner shortcut */}
                    <div
                      onClick={() => {
                        setScannerModalItem(item);
                        setScannerCodeInput(prod.barcodeRaw ?? prod.eanRaw ?? '');
                        setTimeout(() => scannerInputRef.current?.focus(), 150);
                      }}
                      className="cursor-pointer group relative shrink-0"
                      title="Cliquer pour scanner ou saisir le code-barres"
                    >
                      {prod.images && prod.images[0] ? (
                        <img
                          src={prod.images[0]}
                          alt={prod.name}
                          className="h-16 w-16 rounded-lg border border-border bg-white object-contain p-1 shrink-0 group-hover:border-herb-500 transition-colors"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-lg border border-border bg-cream/40 flex items-center justify-center shrink-0 text-ink-ghost group-hover:border-herb-500">
                          <BarcodeIcon className="h-6 w-6 opacity-30" />
                        </div>
                      )}
                      <span className="absolute bottom-1 right-1 rounded bg-black/75 p-0.5 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <ScanLine className="h-3 w-3" />
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {prod.brand && (
                          <Badge tone="muted" className="font-semibold">
                            {prod.brand}
                          </Badge>
                        )}
                        {prod.sku && <span className="font-mono text-xs text-ink-soft">SKU: {prod.sku}</span>}
                        <Badge tone="signal">
                          <span className="flex items-center gap-1 font-semibold">
                            <Boxes className="h-3 w-3" />
                            Pack (Hors matching)
                          </span>
                        </Badge>
                      </div>

                      <h3 className="mt-1 text-sm font-bold text-ink leading-snug truncate" title={prod.name}>
                        {toCapitalizedCase(prod.name)}
                      </h3>

                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
                        <div>
                          <span className="text-ink-faint">Code Woo : </span>
                          <span className="font-mono">{prod.barcodeRaw ?? prod.eanRaw ?? '∅'}</span>
                        </div>

                        {prod.purchasePriceHt !== null && prod.purchasePriceHt !== undefined && (
                          <div>
                            <span className="text-ink-faint">Achat HT : </span>
                            <span className="font-bold text-ink">{money(prod.purchasePriceHt)}</span>
                          </div>
                        )}

                        <div>
                          <span className="text-ink-faint">Opérateur : </span>
                          <span className="font-medium text-ink">{item.decidedBy || 'Opérateur'}</span>
                          {item.decidedAt && (
                            <span className="text-ink-faint ml-1">le {stamp(item.decidedAt)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-wrap items-center gap-2 shrink-0 self-end sm:self-center">
                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => removeFromPackMutation.mutate(item.productId)}
                      disabled={isRemoving}
                      icon={<RotateCcw className="h-3.5 w-3.5 text-herb-600" />}
                      title="Retirer du Pack et renvoyer dans le flux normal de matching"
                    >
                      {isRemoving ? 'Retrait…' : 'Retirer du Pack'}
                    </Button>

                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => {
                        setScannerModalItem(item);
                        setScannerCodeInput(prod.barcodeRaw ?? prod.eanRaw ?? '');
                        setTimeout(() => scannerInputRef.current?.focus(), 150);
                      }}
                      icon={<ScanLine className="h-3.5 w-3.5" />}
                    >
                      Scanner EAN
                    </Button>

                    <Button
                      type="button"
                      variant="soft"
                      size="sm"
                      onClick={() => hideMutation.mutate(item.productId)}
                      disabled={isHiding}
                      icon={<EyeOff className="h-3.5 w-3.5 text-ink-soft" />}
                      title="Masquer le produit sur WooCommerce"
                    >
                      Masquer
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div className="text-xs text-ink-soft">
            Affichage de <strong>{(page - 1) * pageSize + 1}</strong> à{' '}
            <strong>{Math.min(page * pageSize, total)}</strong> sur <strong>{total.toLocaleString('fr-FR')}</strong>{' '}
            produits
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="soft"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || packQuery.isFetching}
            >
              Précédent
            </Button>

            <span className="px-3 py-1 font-mono text-xs font-bold text-ink">
              Page {page} sur {totalPages}
            </span>

            <Button
              type="button"
              variant="soft"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || packQuery.isFetching}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Scanner & Manual Barcode Modal */}
      {scannerModalItem && (
        <ConfirmDialog
          open={true}
          size="lg"
          title="Scanner ou saisir le code-barres"
          confirmLabel={barcodeOverrideMutation.isPending ? 'Enregistrement…' : 'Valider le code EAN'}
          busy={barcodeOverrideMutation.isPending}
          onConfirm={() => {
            if (scannerCodeInput.trim() === '') return;
            barcodeOverrideMutation.mutate({
              productId: scannerModalItem.productId,
              barcode: scannerCodeInput.trim(),
            });
          }}
          onCancel={() => {
            setScannerModalItem(null);
            setScannerCodeInput('');
          }}
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-border bg-paper p-3">
              {scannerModalItem.product.images[0] ? (
                <img
                  src={scannerModalItem.product.images[0]}
                  alt={scannerModalItem.product.name}
                  className="h-16 w-16 rounded object-contain bg-white border border-border p-1 shrink-0"
                />
              ) : (
                <div className="h-16 w-16 rounded bg-card flex items-center justify-center shrink-0 text-ink-ghost">
                  <BarcodeIcon className="h-6 w-6 opacity-30" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-ink truncate">{scannerModalItem.product.name}</p>
                <p className="text-xs text-ink-soft">SKU: {scannerModalItem.product.sku ?? '∅'} · Marque: {scannerModalItem.product.brand ?? '∅'}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="pack-scanner-input" className="block text-xs font-bold text-ink">
                Code-barres EAN-13 (douchette ou clavier) :
              </label>
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-herb-600" />
                <input
                  id="pack-scanner-input"
                  ref={scannerInputRef}
                  type="text"
                  value={scannerCodeInput}
                  onChange={(e) => setScannerCodeInput(e.target.value)}
                  placeholder="Scanner ou taper 13 chiffres…"
                  className="w-full rounded-md border border-border bg-paper pl-9 pr-3 py-2 font-mono text-sm text-ink focus:border-herb-500 focus:outline-hidden"
                  autoFocus
                />
              </div>
              <p className="text-[11px] text-ink-soft">
                La validation retirera le produit du Pack et passera la fiche en statut Approuvé directement.
              </p>
            </div>
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
